import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Text } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { GET_TRIP_PUBLICATION_VIEW } from "./TripViewAPI";
import TripDatesSidebar from "./components/TripDatesSidebar";
import TripOverview from "./components/TripOverview";
import TripProgram from "./components/TripProgram";
import TripViewHeader from "./components/TripViewHeader";
import styles from "./TripView.module.css";

const pickMainPicture = (trip) => {
  if (!trip?.pictures?.length) return "";

  const mainPicture = trip.pictures.find((picture) => picture?.isMain);
  return mainPicture?.link || trip.pictures[0]?.link || "";
};

const TripView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { publicationId } = useParams();
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);

  const { data, loading, error } = useQuery(GET_TRIP_PUBLICATION_VIEW, {
    variables: { tripPublicationId: publicationId },
    skip: !publicationId,
    fetchPolicy: "network-only",
  });

  const publication = data?.QR_GET_TRIP_PUBLICATION?.data;
  const trip = publication?.trip;
  const tripDates = publication?.tripDates || [];
  const selectedTripDate = tripDates[selectedDateIndex] || null;

  useEffect(() => {
    if (!tripDates.length) {
      setSelectedDateIndex(0);
      return;
    }

    if (selectedDateIndex > tripDates.length - 1) {
      setSelectedDateIndex(0);
    }
  }, [tripDates, selectedDateIndex]);

  const heroImage = useMemo(() => pickMainPicture(trip), [trip]);

  const handleEdit = () => {
    if (!publication?.id || !publication?.tripId) return;
    navigate(`/admin/circuits/${publication.id}/${publication.tripId}/edit`);
  };

  if (loading) {
    return (
      <div className={styles.stateMessage}>
        <Text as="p" size="sm">
          {t("tripPublication.tripView.states.loading")}
        </Text>
      </div>
    );
  }

  if (error || !publication?.id) {
    return (
      <div className={styles.stateMessage}>
        <Text as="p" size="sm">
          {t("tripPublication.tripView.states.loadError")}
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <TripViewHeader
        publicationId={publication.id}
        status={publication.status}
        tripReference={trip?.reference}
        onBack={() => navigate("/admin/circuits")}
        onOpenPublication={() => navigate(`/admin/circuits/${publication.id}/publish`)}
        onEdit={handleEdit}
      />

      <div className={styles.contentLayout}>
        <TripOverview trip={trip} heroImage={heroImage} />

        <TripDatesSidebar
          tripDates={tripDates}
          selectedDateIndex={selectedDateIndex}
          onSelectDate={setSelectedDateIndex}
          childsMaxAge={publication?.childsMaxAge}
          babiesMaxAge={publication?.babiesMaxAge}
        />

        <TripProgram steps={trip?.tripProgramDetails || []} selectedTripDate={selectedTripDate} />
      </div>
    </div>
  );
};

export default TripView;
